

import { faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, BoxProps, CircularProgress, Divider, Typography, styled, useTheme } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from '../../contexts/data/MapContext';
import Center from '../common/Center';
import AuthContext from '../../contexts/auth/AuthContext';
import dayjs from 'dayjs';
import CommonContext from '../../contexts/data/CommonContext';


interface IFilterButtonProps {
    loading?: boolean;
    disabled?: boolean;
}

const FilterWrapper = styled(Box, { shouldForwardProp: props => props !== 'disabled' })<BoxProps & { disabled?: boolean }>(({ disabled }) => ({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'white',
    overflow: 'hidden',
    height: '40px',
    borderRadius: '15px',
    marginBottom: '16px',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
    opacity: disabled ? '0.5' : '1',

    "&:hover": {
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    }
}));

const FilterButton: FC<IFilterButtonProps> = ({
    loading,
    disabled
}) => {

    //
    // -- Hooks --
    const theme = useTheme();

    // 
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { filter, openDrawer } = useContext(MapContext);
    const { helpOpen } = useContext(CommonContext);

    const submitter = useMemo(() => {
        if (!user && !filter.bySubmitter.submitterLabel) {
            return null;
        }
        return filter.bySubmitter.submitterLabel ?? MapFilterSubmitter.toString(filter.bySubmitter.submitter) ?? null;
    }, [user, filter]);

    const period = useMemo(() => {
        if (filter.reportingPeriod.periodLabel) {
            return filter.reportingPeriod.periodLabel;
        }
        
        let from = null;
        let to = null;

        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                return `${from?.format('DD/MM/YYYY')} to ${to?.format('DD/MM/YYYY')}`;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                return `${from?.format('DD/MM/YYYY') ?? '-'} to ${to?.format('DD/MM/YYYY') ?? '-'}`;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                return `${from?.format('DD/MM/YYYY') ?? '-'} to ${to?.format('DD/MM/YYYY') ?? '-'}`;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                return `${from.format('DD/MM/YYYY')} to ${to.format('DD/MM/YYYY')}`;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                return `${from.format('DD/MM/YYYY')} to ${to.format('DD/MM/YYYY')}`;

            default: return '-'
        }
    }, [filter.reportingPeriod])

    // TODO: Make the date fields dynamic!!!

    return (
        <FilterWrapper
            disabled={disabled}
            onClick={(disabled || helpOpen) ? undefined : () => openDrawer()}
        >
            <Center 
                sx={{ 
                    backgroundColor: theme.palette.primary.main,
                    padding: '15px',
                    height: '100%'
                }}
            >
                <Center>
                    {
                        loading ?
                            <CircularProgress
                                sx={{ 
                                    color: 'white',
                                    maxHeight: '25px', 
                                    maxWidth: '25px' 
                                }} 
                            /> :
                            <Center width='25px' height='28px'>
                                <FontAwesomeIcon color='white' fontSize='1.2rem' icon={faMagnifyingGlass} />
                            </Center>
                    }
                </Center>
                <Typography 
                    ml='15px' 
                    fontWeight={700} 
                    variant='body1'
                    sx={{ 
                        color: 'white', 
                        textWrap: 'nowrap',
                        display: 'none', 
                        [theme.breakpoints.up(400)]: {
                            display: 'block'
                        },
                        [theme.breakpoints.between('sm', 625)]: {
                            display: 'none'
                        } 
                    }}
                >
                    Select Data
                </Typography>
            </Center>
            <Center
                sx={{
                    padding: '0 15px',
                    display: 'none', 
                    [theme.breakpoints.up('sm')]: {
                        display: 'flex'
                    } 
                }}  
            >
                <Typography fontWeight={600} variant='body1' sx={{ textWrap: 'nowrap' }}>{period}</Typography>
            </Center>
            {
                submitter && 
                <>
                    <Divider orientation='vertical' />
                    <Box
                        sx={{
                            padding: '0 15px',
                            display: 'none', 
                            maxWidth: '350px',
                            overflow: 'hidden',
                            alignItems: 'center',
                            [theme.breakpoints.up('md')]: {
                                display: 'flex'
                            } 
                        }}    
                    >
                        <Typography 
                            fontWeight={600} 
                            variant='body1' 
                            sx={{ 
                                textWrap: 'nowrap', 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%'
                            }}
                        >
                            {submitter}
                        </Typography>
                    </Box>
                </>
            }
        </FilterWrapper>
    )
}

export default FilterButton